import React, { useId } from 'react';

export interface PilotLampProps {
  isOn: boolean;
  color?: string;
  width?: string | number;
  height?: string | number;
}

function resolveLampColors(colorStr: string) {
  const c = colorStr.toLowerCase();

  if (c.includes('danger') || c.includes('ef4444') || c.includes('red') || c === '#f00' || c === '#ff0000') {
    return {
      onCore: '#ffffff',
      onMid: '#ef4444',
      onEdge: '#b91c1c',
      offCore: '#180404',
      offMid: '#380a0a',
      offEdge: '#120202',
      glowColor: '#ef4444',
      haloColor: 'rgba(239, 68, 68, 0.45)',
    };
  }

  if (c.includes('success') || c.includes('22aa22') || c.includes('22c55e') || c.includes('green') || c === '#0f0' || c === '#00ff00') {
    return {
      onCore: '#ffffff',
      onMid: '#22c55e',
      onEdge: '#15803d',
      offCore: '#02180a',
      offMid: '#0a3d1c',
      offEdge: '#021208',
      glowColor: '#22c55e',
      haloColor: 'rgba(34, 197, 94, 0.45)',
    };
  }

  if (c.includes('warning') || c.includes('eab308') || c.includes('facc15') || c.includes('yellow')) {
    return {
      onCore: '#ffffff',
      onMid: '#facc15',
      onEdge: '#ca8a04',
      offCore: '#1a1002',
      offMid: '#382204',
      offEdge: '#140c01',
      glowColor: '#facc15',
      haloColor: 'rgba(250, 204, 21, 0.45)',
    };
  }

  if (c.includes('orange') || c.includes('ea580c') || c.includes('f97316')) {
    return {
      onCore: '#ffffff',
      onMid: '#f97316',
      onEdge: '#c2410c',
      offCore: '#180802',
      offMid: '#381705',
      offEdge: '#120601',
      glowColor: '#f97316',
      haloColor: 'rgba(249, 115, 22, 0.45)',
    };
  }

  if (c.includes('cyan') || c.includes('0891b2') || c.includes('06b6d4')) {
    return {
      onCore: '#ffffff',
      onMid: '#06b6d4',
      onEdge: '#0e7490',
      offCore: '#02141a',
      offMid: '#07323d',
      offEdge: '#010e12',
      glowColor: '#06b6d4',
      haloColor: 'rgba(6, 182, 212, 0.45)',
    };
  }

  if (c.includes('blue') || c.includes('1e40af') || c.includes('3b82f6') || c.includes('38bdf8')) {
    return {
      onCore: '#ffffff',
      onMid: '#38bdf8',
      onEdge: '#1d4ed8',
      offCore: '#021021',
      offMid: '#092547',
      offEdge: '#010a17',
      glowColor: '#38bdf8',
      haloColor: 'rgba(56, 189, 248, 0.45)',
    };
  }

  if (c.includes('white') || c.includes('ffffff')) {
    return {
      onCore: '#ffffff',
      onMid: '#f8fafc',
      onEdge: '#94a3b8',
      offCore: '#0b111e',
      offMid: '#1e293b',
      offEdge: '#070b14',
      glowColor: '#f8fafc',
      haloColor: 'rgba(248, 250, 252, 0.4)',
    };
  }

  return {
    onCore: '#ffffff',
    onMid: colorStr || '#facc15',
    onEdge: '#ca8a04',
    offCore: '#141414',
    offMid: '#292929',
    offEdge: '#0d0d0d',
    glowColor: colorStr || '#facc15',
    haloColor: 'rgba(250, 204, 21, 0.4)',
  };
}

export const PilotLampSvg: React.FC<PilotLampProps> = ({ 
  isOn, 
  color = 'var(--color-warning, #eab308)', 
  width = '100%', 
  height = '100%' 
}) => {
  const uid = useId().replace(/:/g, '');
  const p = resolveLampColors(color);

  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      viewBox="0 0 100 100" 
      width={width} 
      height={height}
      style={{ overflow: 'visible', userSelect: 'none' }}
    >
      <defs>
        {/* Soft fixture drop shadow */}
        <filter id={`lampDropShadow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2.5" stdDeviation="3" floodColor="#000000" floodOpacity="0.6" />
        </filter>

        {/* Outer ambient radiant halo (only when ON) */}
        {isOn && (
          <filter id={`lampAuraFilter-${uid}`} x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="9" result="blur" />
          </filter>
        )}

        {/* Core luminous glow filter (only when ON) */}
        {isOn && (
          <filter id={`lampCoreGlow-${uid}`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feComponentTransfer in="blur" result="glow">
              <feFuncA type="linear" slope="1.8" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode in="glow" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}

        {/* Brushed chrome outer bezel gradient */}
        <linearGradient id={`lampBezelGrad-${uid}`} x1="15%" y1="12%" x2="85%" y2="88%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="20%" stopColor="#d1d5db" />
          <stop offset="45%" stopColor="#9ca3af" />
          <stop offset="70%" stopColor="#4b5563" />
          <stop offset="90%" stopColor="#1f2937" />
          <stop offset="100%" stopColor="#374151" />
        </linearGradient>

        {/* Dark rubber socket gasket */}
        <radialGradient id={`lampSocketGrad-${uid}`} cx="50%" cy="50%">
          <stop offset="70%" stopColor="#1e293b" />
          <stop offset="88%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#05080f" />
        </radialGradient>

        {/* Inner metal reflector rim */}
        <linearGradient id={`lampInnerRim-${uid}`} x1="20%" y1="15%" x2="80%" y2="85%">
          <stop offset="0%" stopColor={isOn ? '#ffffff' : '#cbd5e1'} />
          <stop offset="50%" stopColor={isOn ? p.onEdge : '#64748b'} stopOpacity={isOn ? 0.6 : 1} />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Lamp Dome Glass Gradient */}
        {isOn ? (
          <radialGradient id={`lampGlassGrad-${uid}`} cx="44%" cy="40%" r="55%">
            {/* Blazing white-hot LED emitter center */}
            <stop offset="0%" stopColor="#ffffff" stopOpacity={1} />
            <stop offset="18%" stopColor="#ffffff" stopOpacity={0.95} />
            <stop offset="38%" stopColor={p.onMid} stopOpacity={1} />
            <stop offset="78%" stopColor={p.onEdge} stopOpacity={1} />
            <stop offset="96%" stopColor={p.onEdge} stopOpacity={0.9} />
            <stop offset="100%" stopColor="#0f172a" stopOpacity={0.7} />
          </radialGradient>
        ) : (
          <radialGradient id={`lampGlassGrad-${uid}`} cx="46%" cy="42%" r="54%">
            {/* Dark unlit jewel lens (completely sönük) */}
            <stop offset="0%" stopColor={p.offMid} stopOpacity={0.8} />
            <stop offset="38%" stopColor={p.offCore} stopOpacity={0.95} />
            <stop offset="76%" stopColor={p.offEdge} stopOpacity={1} />
            <stop offset="94%" stopColor="#090d16" stopOpacity={1} />
            <stop offset="100%" stopColor="#030509" stopOpacity={1} />
          </radialGradient>
        )}

        {/* Clip path for the interior lens details */}
        <clipPath id={`lampLensClip-${uid}`}>
          <circle cx="50" cy="50" r="31" />
        </clipPath>
      </defs>

      {/* Outer ambient radiant spill (when ON) */}
      {isOn && (
        <circle 
          cx="50" 
          cy="50" 
          r="46" 
          fill={p.haloColor} 
          filter={`url(#lampAuraFilter-${uid})`} 
          pointerEvents="none" 
        />
      )}

      {/* Main Base with Drop Shadow */}
      <g filter={`url(#lampDropShadow-${uid})`}>
        {/* Outer Metal Bezel Ring */}
        <circle 
          cx="50" 
          cy="50" 
          r="45" 
          fill={`url(#lampBezelGrad-${uid})`} 
          stroke="#111827" 
          strokeWidth="1.2" 
        />

        {/* Bezel Accent Grooves */}
        <circle cx="50" cy="50" r="41" fill="none" stroke="rgba(255, 255, 255, 0.4)" strokeWidth="0.7" />
        <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(0, 0, 0, 0.35)" strokeWidth="0.7" />

        {/* Dark Gasket / Socket Ring */}
        <circle cx="50" cy="50" r="38" fill={`url(#lampSocketGrad-${uid})`} />

        {/* Inner Reflector Rim */}
        <circle 
          cx="50" 
          cy="50" 
          r="34" 
          fill={`url(#lampInnerRim-${uid})`} 
          stroke="#090d16" 
          strokeWidth="1" 
        />
      </g>

      {/* Main Glass Dome Lens */}
      <circle 
        cx="50" 
        cy="50" 
        r="31" 
        fill={`url(#lampGlassGrad-${uid})`} 
        filter={isOn ? `url(#lampCoreGlow-${uid})` : undefined} 
      />

      {/* Internal Fresnel / Diffuser Pattern */}
      <g clipPath={`url(#lampLensClip-${uid})`} pointerEvents="none">
        {/* Concentric Fresnel Diffuser Rings */}
        <circle 
          cx="50" 
          cy="50" 
          r="9" 
          fill="none" 
          stroke={isOn ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.05)'} 
          strokeWidth="0.8" 
          strokeDasharray="3,1.5" 
        />
        <circle 
          cx="50" 
          cy="50" 
          r="16" 
          fill="none" 
          stroke={isOn ? 'rgba(255, 255, 255, 0.38)' : 'rgba(255, 255, 255, 0.04)'} 
          strokeWidth="0.8" 
          strokeDasharray="4,2" 
        />
        <circle 
          cx="50" 
          cy="50" 
          r="23" 
          fill="none" 
          stroke={isOn ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.03)'} 
          strokeWidth="0.8" 
          strokeDasharray="5,2" 
        />

        {/* Radial Prism Ray Accents */}
        <path 
          d="M 50 21 L 50 40 M 50 60 L 50 79 M 21 50 L 40 50 M 60 50 L 79 50 M 29 29 L 43 43 M 57 57 L 71 71 M 71 29 L 57 43 M 43 57 L 29 71" 
          stroke={isOn ? 'rgba(255, 255, 255, 0.28)' : 'rgba(255, 255, 255, 0.03)'} 
          strokeWidth="0.8" 
        />
      </g>

      {/* Surface Curved Glare / Glass Specular Arc */}
      <path 
        d="M 28 35 A 25 25 0 0 1 65 27 A 23 23 0 0 0 33 45 Z" 
        fill="#ffffff" 
        opacity={isOn ? 0.45 : 0.12} 
        pointerEvents="none" 
      />

      {/* Micro Pinpoint Specular Dot */}
      <circle 
        cx="38" 
        cy="34" 
        r="1.8" 
        fill="#ffffff" 
        opacity={isOn ? 0.75 : 0.18} 
        pointerEvents="none" 
      />
    </svg>
  );
};
